import type { CalendarDate } from '@internationalized/date';
import { endOfMonth, getWeeksInMonth, startOfWeek } from '@internationalized/date';
import { useCalendarGrid } from '@react-aria/calendar';
import { useDateFormatter, useLocale } from '@react-aria/i18n';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import type { CalendarPropsBase } from '@react-types/calendar';
import type { DOMProps, StyleProps } from '@react-types/shared';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { CalendarCell } from '../calendar-cell';

interface CalendarMonthProps extends CalendarPropsBase, DOMProps, StyleProps {
  state: CalendarState | RangeCalendarState;
  startDate: CalendarDate;
}

export const CalendarMonth = (props: CalendarMonthProps) => {
  let { state, startDate } = props;
  let { gridProps } = useCalendarGrid(
    {
      ...props,
      endDate: endOfMonth(startDate)
    },
    state
  );

  let dayFormatter = useDateFormatter({ weekday: 'narrow' });
  let dayFormatterLong = useDateFormatter({ weekday: 'long' });

  let { locale } = useLocale();
  let monthStart = startOfWeek(startDate, locale);
  let weeksInMonth = getWeeksInMonth(startDate, locale);

  let [isRangeSelecting, setRangeSelecting] = useState(false);
  let hasAnchorDate = 'anchorDate' in state && state.anchorDate != null;

  if (hasAnchorDate && !isRangeSelecting) {
    setRangeSelecting(true);
  }

  useEffect(() => {
    if (!hasAnchorDate && isRangeSelecting) {
      let raf = requestAnimationFrame(() => setRangeSelecting(false));
      return () => cancelAnimationFrame(raf);
    }
  }, [hasAnchorDate, isRangeSelecting]);

  return (
    <table
      {...gridProps}
      className={classNames('table-style', { 'is-range-selecting-class': isRangeSelecting })}
    >
      <thead className="border-b ">
        <tr>
          {[...new Array(7).keys()].map((index) => {
            let date = monthStart.add({ days: index });
            let dateDay = date.toDate(state.timeZone);
            let day = dayFormatter.format(dateDay);
            let dayLong = dayFormatterLong.format(dateDay);
            return (
              <th
                key={index}
                className="cursor-default px-4 py-2 text-sm text-gray-500"
                style={{ width: `${100 / 7}%` }}
              >
                <VisuallyHidden>{dayLong}</VisuallyHidden>
                <span aria-hidden="true">{day}</span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {[...new Array(7).keys()].map((dayIndex) => (
              <CalendarCell
                key={dayIndex}
                state={state}
                date={monthStart.add({ weeks: weekIndex, days: dayIndex })}
                currentMonth={startDate}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
